import { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Row } from '@tanstack/react-table';
import { Box } from '@mui/material';
import Header from '@/components/Header/Header';
import TableCard from '@/components/Tables/TableCard/TableCard';
import { useNamedPrices } from '@/services/namedPrice/namedPrice.service';
import { NamedPrice } from '@/types/namedPrice.types';
import EditBundleAddOnDrawer from './EditBundleAddOnDrawer/EditBundleAddOnDrawer';
import styles from './BundleAddOns.module.css';

const nameSortFn = (rowA: Row<NamedPrice>, rowB: Row<NamedPrice>) =>
  (rowA.original.name ?? '').localeCompare(rowB.original.name ?? '', undefined, { sensitivity: 'base' });

const BundleAddOns = () => {
  const { t } = useTranslation();
  const { data, isLoading } = useNamedPrices(true);
  const [selectedNamedPrice, setSelectedNamedPrice] = useState<NamedPrice | null>(null);

  const tableData = useMemo(() => {
    const response = data?.data;
    return Array.isArray(response) ? response : [];
  }, [data]);

  const handleCloseDrawer = useCallback(() => setSelectedNamedPrice(null), []);
  // No need to manually refetch - React Query automatically refetches when the mutation invalidates the query

  return (
    <>
      <Header title={t('bundleAddOns.title')} breadcrumb={{ label: t('breadcrumb.backToHome'), to: '/' }} />
      <div className={styles.pageContainer}>
        <div className={styles.tableWrapper} data-testid="bundle-add-ons-table-wrapper">
          {!isLoading && tableData.length === 0 ? (
            <Box pl={{ sm: 5, md: 7 }} pt={{ sm: 2, md: 4 }} fontSize="2.0rem">
              <p>{t('bundleAddOns.table.noBundleAddOnsAvailable')}</p>
            </Box>
          ) : (
            <TableCard<NamedPrice>
              className={styles.addOnsTableCard}
              isLoading={isLoading}
              data={tableData}
              initialSorting={[{ id: 'name', desc: false }]}
              enableSortingRemoval={false}
              columns={[
                {
                  id: 'name',
                  accessorKey: 'name',
                  header: t('bundleAddOns.table.namedPrice'),
                  enableSorting: true,
                  sortingFn: nameSortFn,
                  cell: ({ row, getValue }) => (
                    <button
                      type="button"
                      className={styles.namedPriceLink}
                      onClick={() => setSelectedNamedPrice(row.original)}
                    >
                      {(getValue() as string) ?? '-'}
                    </button>
                  ),
                },
                {
                  accessorKey: 'effectedBundlesCount',
                  header: t('bundleAddOns.table.numberOfBundles'),
                  enableSorting: true,
                  cell: ({ getValue }) => getValue() ?? 0,
                },
              ]}
              noRowsMessage={t('bundleAddOns.table.noBundleAddOnsAvailable') as React.ReactNode}
              enableFilters
            />
          )}
        </div>
      </div>
      <EditBundleAddOnDrawer
        isOpen={!!selectedNamedPrice}
        onClose={handleCloseDrawer}
        namedPrice={selectedNamedPrice}
      />
    </>
  );
};

export default BundleAddOns;
